import { CommonModule } from "@angular/common";
import { Component, computed, inject, model, signal } from "@angular/core";
import { rxResource, toObservable } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import {
    CenterChildDto,
    CenterChildService,
    ChildService,
    PageInfoDto,
    TicketService,
} from "@malirang/api-client";
import { Gender, TicketStatus } from "@malirang/common";
import {
    AlertService,
    Checkbox,
    InfiniteScrollDirective,
    Input,
} from "@malirang/front-share";
import { injectQueryParams } from "ngxtension/inject-query-params";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import { auditTime, combineLatest, map, switchMap } from "rxjs";
import { CustomSelectComponent } from "../../../../../../components/select/custom-select.component";
import { CenterStore } from "../../../../../../stores/center/center.store";
import { CenterRoleStore } from "../../../../../../stores/center/role.store";
import { UserStore } from "../../../../../../stores/user.store";
import { DocumentStore } from "../../../store/document.store";
import { DocumentBackButton } from "../../../components/document-back-button/document-back-button";
import { DocumentSelectChildCard } from "./document-select-child-card/document-select-child-card";

@Component({
    selector: "app-document-select-child",
    templateUrl: "./document-select-child.html",
    styleUrls: ["./document-select-child.css"],
    imports: [
        CommonModule,
        Input,
        CustomSelectComponent,
        DocumentSelectChildCard,
        DocumentBackButton,
        InfiniteScrollDirective,
        Checkbox,
    ],
})
export class DocumentSelectChild {
    readonly centerStore = inject(CenterStore);
    readonly childService = inject(ChildService);
    readonly ticketService = inject(TicketService);
    readonly alertService = inject(AlertService);
    readonly documentStore = inject(DocumentStore);
    readonly centerRoleStore = inject(CenterRoleStore);
    readonly userStore = inject(UserStore);
    readonly router = inject(Router);
    readonly centerChildService = inject(CenterChildService);

    ticketId = injectQueryParams("ticketId");

    query = model<string>("");
    vouchers = model<string[]>([]);
    ticketStatusList = model<string[]>([]);
    teacherIds = model<string[]>([]);
    gender = model<keyof typeof Gender | null>(null);
    onlyMyChildren = model<boolean>(false);

    pageInfo = signal<PageInfoDto | null>(null);
    hasNextPage = computed(() => {
        const pageInfo = this.pageInfo();
        return pageInfo?.totalPages
            ? (pageInfo.pageNo || 0) < pageInfo.totalPages
            : false;
    });

    $filterItems = rxResource({
        stream: () =>
            this.childService.userChildControllerFilter({
                centerId: this.centerStore.currentCenterId(),
            }),
    });
    filterItems = toObservableSignal(this.$filterItems.value);

    documentRead = computed(() => this.centerRoleStore.documentRead());
    documentManageSelf = computed(() =>
        this.centerRoleStore.documentManageSelf(),
    );

    pageNo = model<number>(1);
    // Consolidated request pipeline to avoid multiple rapid requests
    private centerIdSig = computed(() => this.centerStore.currentCenterId());
    private centerId$ = toObservable(this.centerIdSig);
    constructor() {
        combineLatest([
            this.centerId$,
            toObservable(this.query),
            toObservable(this.pageNo),
            toObservable(this.gender),
            toObservable(this.teacherIds),
            toObservable(this.vouchers),
            toObservable(this.ticketStatusList),
            toObservable(this.onlyMyChildren),
        ])
            .pipe(
                auditTime(150),
                switchMap(
                    ([
                        centerId,
                        query,
                        pageNo,
                        gender,
                        teacherIds,
                        vouchers,
                        statusList,
                        onlyMyChildren,
                    ]) => {
                        // 내 아동만 보기 체크 시 현재 사용자의 teacherId 찾기
                        let finalTeacherIds = teacherIds;
                        if (onlyMyChildren) {
                            const currentUserId = this.userStore.user()?.id;
                            const myTeacher =
                                this.filterItems()?.teachers?.find(
                                    (t: any) => t.userId === currentUserId,
                                );
                            if (myTeacher) {
                                finalTeacherIds = [myTeacher.id];
                            }
                        }

                        return this.centerChildService
                            .userCenterChildControllerSearch({
                                centerId,
                                query,
                                pageSize: 15,
                                pageNo,
                                gender: gender!,
                                teacherIds: finalTeacherIds,
                                vouchers,
                                statusList: (statusList as any[]) || [],
                                isDocumentSearch: true,
                            })
                            .pipe(
                                map((res) => {
                                    const isFirstPage = pageNo === 1;
                                    this.childs.set(
                                        isFirstPage
                                            ? res.items
                                            : [...this.childs(), ...res.items],
                                    );
                                    this.pageInfo.set(res.pageInfo);
                                    return res;
                                }),
                            );
                    },
                ),
            )
            .subscribe();
    }
    childs = signal<CenterChildDto[]>([]);
    sortChilds = computed(() =>
        this.childs().sort((a, b) => a.childName.localeCompare(b.childName)),
    );
    currentChild = model<CenterChildDto | null>(null);
    cursor = model<string>("");

    teacherOptions = computed(() => this.filterItems()?.teachers);
    voucherOptions = computed(() => this.filterItems()?.vouchers);
    ticketStatusOptions = Object.entries(TicketStatus)
        .map(([key, value]) => ({
            label: value,
            value: key,
        }))
        .filter((obj) => obj.label !== TicketStatus.BEFORE_COUNSELING);

    currentTicket = this.documentStore.currentTicket;

    filterChange() {
        this.cursor.set("");
        this.childs.set([]);
        this.pageNo.set(1);
    }

    next() {
        this.pageNo.update((n) => n + 1);
    }
}
